import { Search, SendIcon, Square } from 'lucide-react';
import type { Ref } from 'react';
import { useState } from 'react';

import { AutoResizeTextarea } from '@/shared/components/auto-resize-textarea';
import { Button } from '@/shared/components/shadcn/button';
import { cn } from '@/shared/utils';

interface Props {
  onSubmit: (prompt: string, source?: 'free_text' | 'template') => void;
  disabled?: boolean;
  placeholder?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
  value?: string;
  onValueChange?: (value: string) => void;
  attachments?: React.ReactNode;
  leadingContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  submitLabel?: string;
  submitWhenEmpty?: boolean;
  compact?: boolean;
  canSubmitWithoutText?: boolean;
  emptySubmitLabel?: string;
  interruptionSubmit?: boolean;
  inputDisabled?: boolean;
  hideInputArea?: boolean;
}

export function InitialPanelInput({
  onSubmit,
  disabled,
  placeholder = 'Describe a creator',
  inputRef,
  value,
  onValueChange,
  attachments,
  leadingContent,
  footerContent,
  submitLabel,
  submitWhenEmpty = false,
  compact = false,
  canSubmitWithoutText = false,
  emptySubmitLabel = 'Similar',
  interruptionSubmit = false,
  inputDisabled = false,
  hideInputArea = false,
}: Props) {
  const [internalValue, setInternalValue] = useState('');
  const currentValue = value ?? internalValue;
  const setValue = onValueChange ?? setInternalValue;
  const isEmpty = currentValue.trim().length === 0;
  const shouldShowSearchSubmit = canSubmitWithoutText && isEmpty;
  const shouldShowInterruptionSubmit = interruptionSubmit && !isEmpty && !shouldShowSearchSubmit;
  const shouldShowLabeledSubmit = Boolean(submitLabel);
  const shouldShowStopSubmit = submitLabel?.toLowerCase().startsWith('stop') ?? false;
  const canSubmit = !isEmpty || canSubmitWithoutText || submitWhenEmpty;

  const handleSubmit = () => {
    const trimmed = currentValue.trim();
    if (trimmed || canSubmitWithoutText || submitWhenEmpty) {
      onSubmit(trimmed, 'free_text');
      if (value === undefined) {
        setInternalValue('');
      } else {
        setValue('');
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className={cn(
        'border-input bg-background w-full rounded-xl border shadow-sm',
        compact ? 'px-3 py-2' : 'px-3.5 py-2.5',
      )}
    >
      {attachments ? <div className={cn(compact ? 'mb-2' : 'mb-2.5')}>{attachments}</div> : null}

      {hideInputArea ? null : (
        <div
          className={cn(
            compact ? 'min-h-7' : 'min-h-10',
            'transition-opacity',
            isEmpty ? 'opacity-70' : 'opacity-100',
          )}
        >
          <AutoResizeTextarea
            ref={inputRef}
            value={currentValue}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || inputDisabled}
            className={cn(
              'text-foreground w-full border-0 bg-transparent px-0 py-0 text-sm shadow-none outline-none focus-visible:ring-0',
              'placeholder:text-muted-foreground',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
            maxHeight={compact ? 104 : 132}
            minHeight={compact ? 28 : 40}
          />
        </div>
      )}

      <div
        className={cn(
          'flex items-center justify-between gap-3',
          hideInputArea ? 'mt-0' : compact ? 'mt-1.5' : 'mt-2',
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
          {leadingContent ? (
            <div className="shrink-0">{leadingContent}</div>
          ) : (
            <div className="h-8 w-8" />
          )}
          {footerContent ? (
            <div className="min-w-0 flex-1 overflow-hidden">{footerContent}</div>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleSubmit}
            disabled={disabled || !canSubmit}
            size={
              shouldShowLabeledSubmit || shouldShowSearchSubmit || shouldShowInterruptionSubmit
                ? 'sm'
                : 'icon'
            }
            className={cn(
              'shrink-0 rounded-full',
              shouldShowLabeledSubmit || shouldShowSearchSubmit || shouldShowInterruptionSubmit
                ? 'h-8 px-3 text-xs font-semibold'
                : 'size-8',
            )}
          >
            {shouldShowLabeledSubmit ? (
              <>
                {shouldShowStopSubmit ? (
                  <Square className="size-3.5 fill-current" />
                ) : (
                  <SendIcon className="size-3.5" />
                )}
                {submitLabel}
              </>
            ) : shouldShowInterruptionSubmit ? (
              <>
                <Square className="size-3.5 fill-current" />
                Stop and search
              </>
            ) : shouldShowSearchSubmit ? (
              <>
                <Search className="size-3.5" />
                {emptySubmitLabel}
              </>
            ) : (
              <SendIcon className="size-3.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
